import { useCallback } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { TabsState } from "./reducer";
import { useTabsState } from "./use-tabs-state";
import { useTabApiSync } from "./use-tab-api-sync";

export const useTabDnD = (
  state: TabsState,
  actions: ReturnType<typeof useTabsState>["actions"],
  api: ReturnType<typeof useTabApiSync>,
  onSave: (tabs: TabData[]) => void,
) => {
  const { localTabs, draggedIndex } = state;
  const { dragStart, dragEnd, dragEnter, dragLeave, reorder } = actions;

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      dragStart(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", "");
    },
    [dragStart],
  );

  const handleDragEnd = useCallback(() => {
    dragEnd();
  }, [dragEnd]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDragEnter = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      dragEnter(index);
    },
    [dragEnter],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragLeave();
    },
    [dragLeave],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === dropIndex) return;

      const before = [...localTabs];
      reorder(draggedIndex, dropIndex);

      const dragged = before[draggedIndex];
      const target = before[dropIndex];

      // server swap only when both persisted; otherwise just save updated order
      api.swapIfPersisted(dragged, target, () => onSave(before));
      onSave(
        (() => {
          const copy = [...before];
          const [moved] = copy.splice(draggedIndex, 1);
          copy.splice(dropIndex, 0, moved);
          return copy;
        })(),
      );
    },
    [draggedIndex, localTabs, reorder, api, onSave],
  );

  return {
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
  } as const;
};
